// Single source of truth for every synth parameter.
//
// Pure data — no audio, no DOM — so it is trivially unit-testable and shared by
// the engine (routing + smoothing), the UI (control binding + tapers) and the
// preset layer (validation/clamping). Everything downstream keys off `ParamId`.

export type Taper = 'linear' | 'log';

export type ParamGroup =
  | 'osc1'
  | 'osc2'
  | 'osc3'
  | 'filter'
  | 'ampEnv'
  | 'filterEnv'
  | 'lfo'
  | 'mixer'
  | 'voicing'
  | 'master'
  | 'seq';

/**
 * `continuous` — backed by a smooth value (most are real AudioParams; only these
 *                may be `modulatable` and thus appear as LFO destinations).
 * `stepped`    — discrete integer choice (octave, enum index, on/off toggle).
 * `paramRef`   — value is another param's id (or `'none'`): the LFO destination.
 */
export type ParamKind = 'continuous' | 'stepped' | 'paramRef';

interface BaseSpec {
  readonly label: string;
  readonly group: ParamGroup;
  readonly unit: string;
  /** True only for continuous params that the LFO is allowed to target. */
  readonly modulatable: boolean;
  /** Routing hint the engine uses to resolve this param to a node/AudioParam. */
  readonly target?: string;
}

export interface NumericSpec extends BaseSpec {
  readonly kind: 'continuous' | 'stepped';
  readonly min: number;
  readonly max: number;
  readonly default: number;
  readonly taper: Taper;
  /** Quantisation step for `stepped` params (defaults to 1). */
  readonly step?: number;
}

export interface ParamRefSpec extends BaseSpec {
  readonly kind: 'paramRef';
  readonly modulatable: false;
  /** `'none'` or a valid ParamId. */
  readonly default: string;
}

export type ParamSpec = NumericSpec | ParamRefSpec;

// Sentinel used by `paramRef` params (the LFO destination) to mean "unrouted".
export const NO_DEST = 'none';

const c = (
  label: string,
  group: ParamGroup,
  min: number,
  max: number,
  def: number,
  unit: string,
  opts: { taper?: Taper; modulatable?: boolean; target?: string } = {},
): NumericSpec => ({
  kind: 'continuous',
  label,
  group,
  min,
  max,
  default: def,
  unit,
  taper: opts.taper ?? 'linear',
  modulatable: opts.modulatable ?? false,
  ...(opts.target !== undefined ? { target: opts.target } : {}),
});

const s = (
  label: string,
  group: ParamGroup,
  min: number,
  max: number,
  def: number,
  unit = '',
  step = 1,
): NumericSpec => ({
  kind: 'stepped',
  label,
  group,
  min,
  max,
  default: def,
  unit,
  step,
  taper: 'linear',
  modulatable: false,
});

// Per-oscillator blocks (osc1 = analog/PWM, osc2 = wavetable, osc3 = FM 4-op).
// Written out explicitly so each key is a literal in `ParamId`. The default
// patch starts with only osc1 audible (osc2/osc3 levels at 0).
export const PARAMS = {
  'osc1.octave': s('Octave', 'osc1', -2, 2, 0, 'oct'),
  'osc1.shape': c('Shape', 'osc1', 0, 1, 0.5, '', { modulatable: true }),
  'osc1.detune': c('Detune', 'osc1', -50, 50, 0, 'cents', { modulatable: true }),
  'osc1.level': c('Level', 'osc1', 0, 1, 0.8, '', { modulatable: true }),
  'osc1.drive': c('Drive', 'osc1', 0, 1, 0, '', { modulatable: true }),

  'osc2.octave': s('Octave', 'osc2', -2, 2, 0, 'oct'),
  'osc2.shape': c('Shape', 'osc2', 0, 1, 0.5, '', { modulatable: true }),
  'osc2.detune': c('Detune', 'osc2', -50, 50, 0, 'cents', { modulatable: true }),
  'osc2.level': c('Level', 'osc2', 0, 1, 0, '', { modulatable: true }),
  'osc2.drive': c('Drive', 'osc2', 0, 1, 0, '', { modulatable: true }),

  'osc3.octave': s('Octave', 'osc3', -2, 2, 0, 'oct'),
  'osc3.shape': c('Shape', 'osc3', 0, 1, 0.5, '', { modulatable: true }),
  'osc3.detune': c('Detune', 'osc3', -50, 50, 0, 'cents', { modulatable: true }),
  'osc3.level': c('Level', 'osc3', 0, 1, 0, '', { modulatable: true }),
  'osc3.drive': c('Drive', 'osc3', 0, 1, 0, '', { modulatable: true }),

  'filter.cutoff': c('Cutoff', 'filter', 20, 18000, 12000, 'Hz', {
    taper: 'log',
    modulatable: true,
  }),
  'filter.resonance': c('Resonance', 'filter', 0, 1, 0.1, '', { modulatable: true }),
  'filter.envAmount': c('Env Amt', 'filter', -1, 1, 0, '', { modulatable: true }),

  'ampEnv.attack': c('Attack', 'ampEnv', 0.001, 5, 0.005, 's', { taper: 'log' }),
  'ampEnv.decay': c('Decay', 'ampEnv', 0.001, 5, 0.2, 's', { taper: 'log' }),
  'ampEnv.sustain': c('Sustain', 'ampEnv', 0, 1, 0.8, ''),
  'ampEnv.release': c('Release', 'ampEnv', 0.001, 5, 0.3, 's', { taper: 'log' }),

  'filterEnv.attack': c('Attack', 'filterEnv', 0.001, 5, 0.005, 's', { taper: 'log' }),
  'filterEnv.decay': c('Decay', 'filterEnv', 0.001, 5, 0.4, 's', { taper: 'log' }),
  'filterEnv.sustain': c('Sustain', 'filterEnv', 0, 1, 0.3, ''),
  'filterEnv.release': c('Release', 'filterEnv', 0.001, 5, 0.4, 's', { taper: 'log' }),

  'lfo.rate': c('Rate', 'lfo', 0.01, 30, 5, 'Hz', { taper: 'log' }),
  'lfo.depth': c('Depth', 'lfo', 0, 1, 0, ''),
  // 0 sine · 1 triangle · 2 square · 3 ramp · 4 sample & hold
  'lfo.shape': s('Shape', 'lfo', 0, 4, 0),
  'lfo.sync': s('Sync', 'lfo', 0, 1, 0),
  'lfo.dest': {
    kind: 'paramRef',
    label: 'Destination',
    group: 'lfo',
    unit: '',
    modulatable: false,
    default: NO_DEST,
  } satisfies ParamRefSpec,

  // 0 = poly (6 voices) · 1 = mono (last-note priority + glide)
  'voicing.mode': s('Mode', 'voicing', 0, 1, 0),
  'voicing.glide': c('Glide', 'voicing', 0, 2, 0.05, 's', { taper: 'log' }),

  'master.volume': c('Volume', 'master', 0, 1, 0.8, '', {
    taper: 'log',
    modulatable: true,
  }),
  'master.drive': c('Drive', 'master', 0, 1, 0, '', { modulatable: true }),

  'seq.bpm': c('BPM', 'seq', 20, 300, 120, 'bpm'),
  'seq.swing': c('Swing', 'seq', 0, 0.75, 0, ''),
  'seq.steps': s('Steps', 'seq', 1, 8, 8),
} satisfies Record<string, ParamSpec>;

export type ParamId = keyof typeof PARAMS;

export type PatchValue = number | string;

/** A complete patch: a flat map of every paramId to its value. */
export type Patch = Record<ParamId, PatchValue>;

const PARAM_IDS = Object.keys(PARAMS) as ParamId[];

export function allParamIds(): readonly ParamId[] {
  return PARAM_IDS;
}

export function getSpec(id: ParamId): ParamSpec {
  return PARAMS[id];
}

/** Like `getSpec`, but narrowed to a numeric spec (throws for the paramRef). */
export function getNumericSpec(id: ParamId): NumericSpec {
  const spec = PARAMS[id];
  if (spec.kind === 'paramRef') {
    throw new Error(`Param "${id}" is not numeric`);
  }
  return spec;
}

export function isParamId(id: string): id is ParamId {
  return Object.prototype.hasOwnProperty.call(PARAMS, id);
}

/** Params the LFO may target — populates the destination selector. */
export function modulatableParamIds(): ParamId[] {
  return PARAM_IDS.filter((id) => PARAMS[id].modulatable);
}

/** Clamp (and for stepped params, quantise) a numeric value into a spec's range. */
export function clampNumeric(spec: NumericSpec, value: number): number {
  if (!Number.isFinite(value)) return spec.default;
  let v = Math.min(spec.max, Math.max(spec.min, value));
  if (spec.kind === 'stepped') {
    const step = spec.step ?? 1;
    v = Math.round(v / step) * step;
    v = Math.min(spec.max, Math.max(spec.min, v));
  }
  return v;
}

/**
 * Coerce an arbitrary value to a valid value for `id`, falling back to the
 * param default when input is unusable. Untrusted input is always run through
 * this before reaching the engine.
 */
export function coerceValue(id: ParamId, value: unknown): PatchValue {
  const spec = PARAMS[id];
  if (spec.kind === 'paramRef') {
    if (value === NO_DEST) return NO_DEST;
    if (typeof value === 'string' && isParamId(value) && PARAMS[value].modulatable) {
      return value;
    }
    return spec.default;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return clampNumeric(spec, n);
}
